import { Duration } from "luxon";

import { nanoSecToSecLossy } from "./time";

/**
 * Format a duration in seconds as `Xd Yh Zm Ss Mms`, trimming leading
 * and trailing zero units (e.g. `60.3` → `"1m 0s 300ms"`). Days are
 * uncapped; zero is rendered explicitly as `"0ms"` to distinguish empty
 * from absent. Throws on negative input.
 */
export function formatDuration(durationInSeconds: number) {
  /**
   * @TODO update implementation to use Intl.DurationFormat over luxon
   */

  if (durationInSeconds < 0) {
    throw new Error("Duration is negative");
  }

  // Special-cased because the trimming below finds no non-zero unit to anchor
  // on and would collapse to "", which reads as a missing value rather than a
  // genuine zero. Matches the "0ms" that any sub-millisecond duration renders.
  if (durationInSeconds === 0) {
    return "0ms";
  }

  const dur = Duration.fromObject({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: durationInSeconds,
    milliseconds: 0,
  });

  // Normalize spreads the seconds into the time units
  // explicitly described in the duration object
  const normalized = dur.normalize();

  const timeUnitsTemplateStrings = ["d'd'", "h'h'", "m'm'", "s's'", "S'ms'"];
  const timeUnits = [
    normalized.days,
    normalized.hours,
    normalized.minutes,
    normalized.seconds,
    normalized.milliseconds,
  ];

  const firstNonZeroIndex = timeUnits.findIndex((timeUnit) => timeUnit > 0);

  let lastNonZeroIndex = -1;
  for (let i = timeUnits.length - 1; i >= 0; i--) {
    if (timeUnits[i] > 0) {
      lastNonZeroIndex = i;
      break;
    }
  }

  const templateString = timeUnitsTemplateStrings
    .slice(firstNonZeroIndex, lastNonZeroIndex + 1)
    .join(" ");

  return dur.toFormat(templateString);
}

/**
 * Format a nanosecond duration via {@link formatDuration}. Returns `""`
 * for missing or negative values so table cells stay blank rather than
 * showing a negative span. The ns→seconds step uses
 * {@link nanoSecToSecLossy}; sub-microsecond detail is dropped.
 */
export function formatNanoDuration(value: bigint | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (value < 0n) {
    return "";
  }
  return formatDuration(nanoSecToSecLossy(value));
}

const SHORT_UNITS: [suffix: string, seconds: number][] = [
  ["d", 86_400],
  ["h", 3_600],
  ["m", 60],
  ["s", 1],
];

/**
 * Format a duration in seconds as its two largest units, for a glance rather
 * than a measurement: `"8h 33m"`, `"10m 8s"`, `"42s"`, `"1d 2h"`. The
 * smaller unit is left out when it is zero (`"2h"`), smaller ones are
 * dropped rather than rounded, and anything under a second is `"<1s"`.
 * Throws on negative input.
 */
export function formatShortDuration(durationInSeconds: number): string {
  if (durationInSeconds < 0) {
    throw new Error("Duration is negative");
  }
  let rest = Math.floor(durationInSeconds);
  const parts = SHORT_UNITS.map(([suffix, size]) => {
    const count = Math.floor(rest / size);
    rest -= count * size;
    return { suffix, count };
  });
  const first = parts.findIndex((part) => part.count > 0);
  if (first === -1) {
    return "<1s";
  }
  return parts
    .slice(first, first + 2)
    .filter((part, i) => i === 0 || part.count > 0)
    .map((part) => `${part.count}${part.suffix}`)
    .join(" ");
}

/**
 * {@link formatShortDuration} for a nanosecond duration, `""` when it is
 * missing or negative.
 */
export function formatShortNanoDuration(
  value: bigint | null | undefined,
): string {
  if (value === null || value === undefined || value < 0n) {
    return "";
  }
  return formatShortDuration(nanoSecToSecLossy(value));
}
